import { QueryKey, useQueries } from '@tanstack/react-query'

import * as R from 'core/helpers/remeda'
import { OperationData } from 'core/types/makeMutationHook'
import {
  PaginatedOperation,
  QueryHookArguments,
  SingletonOperation,
  getQueryOptions,
} from 'core/types/makeQueryHook'
import {
  companyLoanConfigurationsGet,
  companyRoleGetAll,
  getPersonDocument,
  getPromiseToPayPlansByLoan,
  loanGet,
  populationMembersList,
} from 'core/types/operations'

type Response<Resp> = Required<Resp> extends Required<PaginatedOperation>
  ? Resp extends PaginatedOperation
    ? Resp['data']
    : never
  : Resp extends SingletonOperation
  ? Array<NonNullable<Resp['data']>>
  : Array<NonNullable<Resp>>

const makeMultipleHook =
  <Resp, Key extends QueryKey, Vars extends { query?: unknown }>(
    data: OperationData<Resp, Key, Vars>,
  ) =>
  (
    argsList: Array<QueryHookArguments<Resp, Key, Vars>>,
  ): { isLoading: boolean; data: Response<Resp> } =>
    useQueries({
      queries: R.map(argsList, (args) => getQueryOptions(data, args) as any),
      combine: (results: Array<any>) => ({
        isLoading: results.some((result) => Boolean(result?.isLoading)),
        data: R.flatMap(results, (result) => {
          const data = result?.data

          return !R.isDefined(data)
            ? []
            : !R.isObject(data)
            ? [data]
            : 'count' in data && 'data' in data && R.isArray((data as any).data)
            ? (data as any).data
            : 'data' in data
            ? [(data as any).data]
            : [data]
        }) as Response<Resp>,
      }),
    } as any)

export const useGetMultipleLoans = makeMultipleHook(loanGet)

export const useGetMultipleLoanConfigurations = makeMultipleHook(
  companyLoanConfigurationsGet,
)

export const useGetMultipleDocuments = makeMultipleHook(getPersonDocument)

export const useGetMultiplePromiseToPay = makeMultipleHook(
  getPromiseToPayPlansByLoan,
)

export const useGetMultiplePopulationMembersList = makeMultipleHook(
  populationMembersList,
)

export const useGetMultipleCompanyRoles = makeMultipleHook(companyRoleGetAll)
